/**
 * Admin AI Calling — create voice agent (testable, no axios / Indus).
 * Voice lives on Config (voice_id). Create POSTs /api/ai-calls/agents, never click2call.
 */

import {
  canonicalizeIndusVoiceId,
  normalizeTranscriptLanguage,
} from '@/utils/ai-calling-prompt-defaults'
import { extractBoundAgentId } from '@/utils/ai-calling-initiate'
import {
  validateCreateAgentInput,
  resolveConfiguredLanguage,
  resolveConfiguredVoice,
} from '@/utils/ai-calling-config-schema'

/** Documented ATS proxy. createIndusAgent POSTs this — not click2call. */
export const AI_AGENT_CREATE_PATH = 'ai-calls/agents'

export function isClick2CallPath(path) {
  return /click2call/i.test(String(path || ''))
}

export function isAgentCreatePath(path) {
  const p = String(path || '').replace(/^\//, '')
  if (isClick2CallPath(p)) return false
  return /(^|\/)ai-calls\/agents\/?$/i.test(p)
}

/**
 * Create Config language. English / Hindi stay as codes.
 * Multi is not a transcript language — empty so create fails visibly.
 */
export function resolveCreateAgentLanguage(value) {
  const n = normalizeTranscriptLanguage(value)
  if (!n || n === 'multi') return ''
  return n
}

/** Wizard required fields — name, language, voice, opening, script. */
export function canSubmitCreateAgent(wizard = {}) {
  const src = wizard && typeof wizard === 'object' ? wizard : {}
  const name = String(src.name || src.agent_name || '').trim()
  const language = resolveConfiguredLanguage(src) || resolveCreateAgentLanguage(
    src.language || src.transcript_language,
  )
  const voice = resolveConfiguredVoice(src) || String(src.voice || src.voice_id || '').trim()
  const firstMessage = String(
    src.firstMessage || src.first_message || src.starting_instructions || '',
  ).trim()
  const systemPrompt = String(
    src.previewSystemPrompt || src.systemPrompt || src.system_prompt || '',
  ).trim()
  return !!(name && language && voice && firstMessage && systemPrompt)
}

/** Created agent id from ATS/Indus envelope. Keeps AGT_, never rewrites to ACT_. */
export function extractCreatedAgentId(res) {
  return extractBoundAgentId(res)
}

export function isIndusCreatedAgentId(id) {
  return /^AGT_/i.test(String(id || '').trim())
}

/**
 * Admin wizard → input for buildCreateAgentPayload.
 * Language/voice/schema come from the form or org config — never invented.
 */
export function buildAdminCreateAgentInput(wizard = {}) {
  const src = wizard && typeof wizard === 'object' ? wizard : {}
  const name = String(src.name || '').trim()
  const description = String(src.description || '').trim()
  const language = resolveConfiguredLanguage({
    language: src.language,
    transcript_language: src.transcript_language,
    formLanguage: src.formLanguage,
    voice: src.voice || src.voice_id,
    config: src.config,
    agent_config: src.agent_config,
  }) || resolveCreateAgentLanguage(src.language || src.transcript_language)
  const voice = resolveConfiguredVoice({
    voice: src.voice,
    voice_id: src.voice_id,
    config: src.config,
    agent_config: src.agent_config,
  }) || canonicalizeIndusVoiceId(src.voice)
  const preset = String(src.preset || '')
  const callType = preset === 'drive_en'
    ? 'drive'
    : (preset === 'screening_en' ? 'fresh' : (src.callType || src.call_type || ''))
  const input = {
    agent_in: {
      agent_name: name,
      agent_type: 'OUTBOUND',
      agent_description: description || null,
    },
    language,
    voice,
    realtime: true,
    callType,
    config: src.config,
    call_infield: src.call_infield,
    call_outcome: src.call_outcome,
    agent_config: {
      system_prompt: String(src.previewSystemPrompt || src.systemPrompt || '').trim(),
      starting_instructions: String(src.firstMessage || '').trim(),
      language,
      voice,
    },
  }
  return input
}

export function createAgentSubmitErrors(wizard = {}) {
  return validateCreateAgentInput(buildAdminCreateAgentInput(wizard)).errors
}

function asPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

function omitNullSchemaEnums(item) {
  if (!item || typeof item !== 'object') return item
  const next = { ...item }
  if (next.field_enum == null) delete next.field_enum
  if (next.outcome_enum == null) delete next.outcome_enum
  return next
}

function firstSchemaObject(value) {
  if (Array.isArray(value)) {
    const item = value.find(entry => entry && typeof entry === 'object')
    return item ? omitNullSchemaEnums(item) : null
  }
  if (value && typeof value === 'object') return omitNullSchemaEnums(value)
  return null
}

/**
 * Indus POST /api/agents/create `agent_in`.
 * First wire body uses 09-07 / ATS / Python: agent_name + agent_type.
 * `name` / `description` is a fallback only (AI_CALLING_BACKEND.md + ATS upsert).
 * Omit empty description (null 400s). Never send both name and agent_name.
 */
export function buildIndusCreateAgentIn(built = {}, opts = {}) {
  const src = asPlainObject(built)
  const agentIn = asPlainObject(src.agent_in)
  const name = String(
    agentIn.agent_name || agentIn.name || src.agent_name || src.name || '',
  ).trim()
  const agentType = String(agentIn.agent_type || src.agent_type || 'OUTBOUND').trim().toUpperCase()
    || 'OUTBOUND'
  const description = String(
    agentIn.agent_description || agentIn.description || src.agent_description || src.description || '',
  ).trim()
  const type = agentType === 'INBOUND' || agentType === 'WHATSAPP' ? agentType : 'OUTBOUND'
  if (opts.naming === 'name') {
    const out = { name, agent_type: type }
    if (description) out.description = description
    return out
  }
  const out = { agent_name: name, agent_type: type }
  if (description) out.agent_description = description
  return out
}

/**
 * Indus create `agent_config` subset. Official 09-07 / ATS forward:
 * system_prompt + starting_instructions + tts_config.voice_id.
 * Do **not** send language / voice / voice_id / realtime / tts_config.voice
 * — extra=forbid → Indus 422 / ATS 400 "Failed to create Indus agent".
 * stt_config is optional and not required on create.
 */
export function buildIndusCreateAgentConfig(built = {}, opts = {}) {
  const includeTts = opts.includeTts !== false
  const includeStt = opts.includeStt === true
  const src = asPlainObject(built)
  const cfg = asPlainObject(src.agent_config)
  const tts = asPlainObject(cfg.tts_config)
  const stt = asPlainObject(cfg.stt_config)
  const config = {
    system_prompt: String(cfg.system_prompt || src.system_prompt || src.systemPrompt || '').trim(),
    starting_instructions: String(
      cfg.starting_instructions || src.starting_instructions || src.firstMessage || src.first_message || '',
    ).trim(),
  }
  const voice = String(
    tts.voice_id || tts.voice || cfg.voice_id || cfg.voice || src.voice || src.voice_id || '',
  ).trim()
  const language = String(
    stt.language || cfg.language || src.language || '',
  ).trim()
  if (includeTts && voice) {
    config.tts_config = { voice_id: voice }
  }
  if (includeStt && language) {
    config.stt_config = { language }
  }
  return config
}

export function isCompleteIndusCreateConfig(cfg) {
  const src = asPlainObject(cfg)
  return !!(String(src.system_prompt || '').trim() && String(src.starting_instructions || '').trim())
}

function slimIndusInfield(item) {
  if (!item || typeof item !== 'object') return null
  const field_name = String(item.field_name || item.name || '').trim()
  if (!field_name) return null
  const out = {
    field_name,
    field_type: String(item.field_type || item.type || 'TEXT').trim().toUpperCase() || 'TEXT',
    is_visible: item.is_visible !== false && item.visible !== false,
  }
  if (item.field_enum != null) out.field_enum = item.field_enum
  return out
}

function slimIndusOutcome(item) {
  if (!item || typeof item !== 'object') return null
  const outcome_name = String(item.outcome_name || item.name || '').trim()
  if (!outcome_name) return null
  const outcome_type = String(item.outcome_type || item.type || 'TEXT').trim().toUpperCase() || 'TEXT'
  const out = {
    outcome_name,
    outcome_description: String(item.outcome_description || item.description || '').trim() || outcome_name,
    outcome_type,
    is_visible: item.is_visible !== false && item.visible !== false,
  }
  if (item.outcome_enum != null) out.outcome_enum = item.outcome_enum
  return out
}

function withSchema(base, infield, outcome) {
  const next = { ...base }
  if (infield) next.call_infield = infield
  if (outcome) next.call_outcome = outcome
  return next
}

function withAgentConfig(agentIn, config) {
  const next = { agent_in: agentIn }
  if (isCompleteIndusCreateConfig(config)) {
    next.agent_config = config
  }
  return next
}

/**
 * POST bodies for /api/ai-calls/agents.
 * ATS `sanitizeCreatePayload` forwards agent_in + agent_config (+ optional
 * call_infield / call_outcome) as-is. It does not peel arrays or rewrite names.
 * First body is the 09-07 contract. Never emit agent_config: {} or flatPrompts.
 */
export function buildIndusCreatePostBodies(built = {}) {
  const agentInOfficial = buildIndusCreateAgentIn(built, { naming: 'agent_name' })
  const agentInName = buildIndusCreateAgentIn(built, { naming: 'name' })
  const firstInfield = slimIndusInfield(firstSchemaObject(built && built.call_infield))
  const firstOutcome = slimIndusOutcome(firstSchemaObject(built && built.call_outcome))
  const ttsConfig = buildIndusCreateAgentConfig(built, { includeTts: true, includeStt: false })
  const promptsConfig = buildIndusCreateAgentConfig(built, { includeTts: false, includeStt: false })
  const official = withSchema(
    withAgentConfig(agentInOfficial, ttsConfig),
    firstInfield,
    firstOutcome,
  )
  return {
    official,
    officialNoSchema: withAgentConfig(agentInOfficial, ttsConfig),
    officialName: withSchema(
      withAgentConfig(agentInName, ttsConfig),
      firstInfield,
      firstOutcome,
    ),
    officialNameNoSchema: withAgentConfig(agentInName, ttsConfig),
    promptsOnly: withAgentConfig(agentInOfficial, promptsConfig),
  }
}

/** Console / tests — keys + lengths. Use describeCreatePost for the real body. */
export function summarizeCreatePost(body = {}) {
  const src = asPlainObject(body)
  const agentIn = asPlainObject(src.agent_in)
  const cfg = asPlainObject(src.agent_config)
  const tts = asPlainObject(cfg.tts_config)
  return {
    keys: Object.keys(src),
    agent_in: { ...agentIn },
    agent_in_keys: Object.keys(agentIn),
    agent_config_keys: Object.keys(cfg),
    tts_config_keys: Object.keys(tts),
    stt_config_keys: Object.keys(asPlainObject(cfg.stt_config)),
    system_prompt_len: String(cfg.system_prompt || src.system_prompt || '').trim().length,
    starting_instructions_len: String(
      cfg.starting_instructions || src.starting_instructions || '',
    ).trim().length,
    tts_voice_id: tts.voice_id || null,
    has_call_infield: src.call_infield != null,
    has_call_outcome: src.call_outcome != null,
    top_level_prompts: !!(src.system_prompt || src.starting_instructions),
  }
}

/** Failure console — full agent_in + agent_config, not key counts. */
export function describeCreatePost(body = {}) {
  const src = asPlainObject(body)
  const cfg = asPlainObject(src.agent_config)
  const extraConfigKeys = Object.keys(cfg).filter(k =>
    !['system_prompt', 'starting_instructions', 'tts_config'].includes(k),
  )
  return {
    keys: Object.keys(src),
    agent_in: { ...asPlainObject(src.agent_in) },
    agent_config: Object.keys(cfg).length
      ? {
        system_prompt: cfg.system_prompt || '',
        starting_instructions: cfg.starting_instructions || '',
        tts_config: { ...asPlainObject(cfg.tts_config) },
        extra_keys: extraConfigKeys,
      }
      : null,
    call_infield: src.call_infield != null ? src.call_infield : null,
    call_outcome: src.call_outcome != null ? src.call_outcome : null,
    top_level_prompts: src.system_prompt || src.starting_instructions
      ? {
        system_prompt: src.system_prompt || '',
        starting_instructions: src.starting_instructions || '',
      }
      : null,
  }
}
